import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  apiDeleteExpense,
  apiGetExpenses,
  apiUpdateExpense,
  type Expense,
} from "./expenseApi";

type BusinessFilter = "PICSHOT" | "PICO_KIDS" | "ALL";
type PaymentType = "CASH" | "ACCOUNT" | "QPAY";

const picshotCategories = [
  "Хэвлэлийн цаас",
  "Жааз",
  "Canvas",
  "Хор",
  "Дэвсгэр",
  "Тоног төхөөрөмж",
  "Түрээс",
  "Цалин",
  "Цэвэрлэгээ",
  "Бусад",
];

const picoKidsCategories = [
  "Мах",
  "Ногоо",
  "Нарийн ногоо",
  "Гурил",
  "Будаа",
  "Гоймон",
  "Амтлагч",
  "Ундаа",
  "Тоглоом",
  "Цэвэрлэгээ",
  "Ажилчдын цалин",
  "Түрээс",
  "Цахилгаан / Ус",
  "Бусад",
];

export default function ExpenseList() {
  const [items, setItems] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [business, setBusiness] = useState<BusinessFilter>("PICSHOT");
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [category, setCategory] = useState<string | undefined>(undefined);

  // row selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetExpenses({
        businessType: business === "ALL" ? undefined : business,
        date: date ? date.format("YYYY-MM-DD") : undefined,
        category: category || undefined, // ✅ category filter
      });

      setItems(data);
      setSelectedRowKeys([]); // filter солигдоход selection цэвэрлэ
    } catch (e: any) {
      message.error(e?.message || "Fetch error");
    } finally {
      setLoading(false);
    }
  }, [business, category, date]);

  // ✅ НЭГ л useEffect хангалттай
  useEffect(() => {
    load();
  }, [load]);

  const selectedSum = useMemo(() => {
    const set = new Set(selectedRowKeys);
    return items
      .filter((x) => set.has(x._id))
      .reduce((acc, x) => acc + (x.amount || 0), 0);
  }, [items, selectedRowKeys]);

  const openEdit = (row: Expense) => {
    setEditing(row);
    setEditOpen(true);
    form.setFieldsValue({
      businessType: row.businessType,
      expenseCategory: row.expenseCategory,
      amount: row.amount,
      paymentType: row.paymentType,
      date: dayjs(row.date),
      description: row.description || "",
      supplier: row.supplier || "",
    });
  };

  const saveEdit = async () => {
    try {
      const v = await form.validateFields();
      if (!editing) return;

      await apiUpdateExpense(editing._id, {
        businessType: v.businessType,
        expenseCategory: v.expenseCategory,
        amount: v.amount,
        paymentType: v.paymentType,
        date: v.date.format("YYYY-MM-DD"),
        description: v.description || "",
        supplier: v.supplier || "",
      });

      message.success("Засагдлаа");
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || "Edit error");
    }
  };

  const doDelete = async (id: string) => {
    try {
      await apiDeleteExpense(id);
      message.success("Устгагдлаа");
      await load();
    } catch (e: any) {
      message.error(e?.message || "Delete error");
    }
  };

  const categoryOptions = useMemo(() => {
    const list =
      business === "PICSHOT"
        ? picshotCategories
        : business === "PICO_KIDS"
        ? picoKidsCategories
        : [...picshotCategories, ...picoKidsCategories];

    // давхардлыг цэвэрлэ
    return Array.from(new Set(list)).map((c) => ({ value: c, label: c }));
  }, [business]);

  const columns = [
    {
      title: "Огноо",
      dataIndex: "date",
      key: "date",
      render: (d: string) => dayjs(d).format("YYYY-MM-DD"),
    },
    {
      title: "Бизнес",
      dataIndex: "businessType",
      key: "businessType",
      render: (v: string) =>
        v === "PICSHOT" ? (
          <Tag color="green">PicShot</Tag>
        ) : (
          <Tag color="lime">Pico Kids</Tag>
        ),
    },
    { title: "Төрөл", dataIndex: "expenseCategory", key: "expenseCategory" },
    {
      title: "Төлбөр",
      dataIndex: "paymentType",
      key: "paymentType",
      render: (p: PaymentType) =>
        p === "CASH" ? "Бэлэн" : p === "ACCOUNT" ? "Данс" : "QPay",
    },
    {
      title: "Тайлбар",
      dataIndex: "description",
      render: (t?: string) => {
        if (!t) return "-";

        return (
          <Tooltip
            title={<pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{t}</pre>}
          >
            <span
              style={{
                display: "inline-block",
                maxWidth: 240,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "pointer",
              }}
            >
              {t}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Нийлүүлэгч",
      dataIndex: "supplier",
      key: "supplier",
      render: (t: string) => t || "-",
    },
    {
      title: "Дүн",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (n: number) => n.toLocaleString(),
    },
    {
      title: "Үйлдэл",
      key: "action",
      render: (_: any, row: Expense) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Устгах уу?"
            okText="Тийм"
            cancelText="Үгүй"
            onConfirm={() => doDelete(row._id)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Typography.Title level={3}>Зардлын жагсаалт</Typography.Title>

      {/* ✅ Filters (нэг мөр) */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Radio.Group
          value={business}
          onChange={(e) => {
            setBusiness(e.target.value);
            setCategory(undefined); // бизнес солигдоход category reset
          }}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="PICSHOT">PicShot</Radio.Button>
          <Radio.Button value="PICO_KIDS">Pico Kids</Radio.Button>
          <Radio.Button value="ALL">Бүгд</Radio.Button>
        </Radio.Group>

        <DatePicker
          value={date}
          onChange={setDate}
          allowClear
          placeholder="Огноо"
        />

        <Select
          allowClear
          placeholder="Төрлөөр шүүх"
          value={category}
          style={{ width: 200 }}
          onChange={(v) => setCategory(v)}
          options={categoryOptions}
        />
      </Space>

      <Table
        rowKey="_id"
        loading={loading}
        dataSource={items}
        columns={columns as any}
        bordered
        className="custom-table"
        style={{ maxWidth: "100vw" }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        pagination={{ pageSize: 20 }}
      />
      <div style={{ marginBottom: 8 }}>
        Сонгосон: <b>{selectedRowKeys.length}</b> мөр — Нийт дүн:{" "}
        <b>{selectedSum.toLocaleString()}₮</b>
      </div>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        title="Зардал засах"
        onCancel={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        onOk={saveEdit}
        okText="Хадгалах"
        cancelText="Болих"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="businessType"
            label="Бизнес"
            rules={[{ required: true }]}
          >
            <Select
              disabled
              options={[
                { value: "PICSHOT", label: "PicShot" },
                { value: "PICO_KIDS", label: "Pico Kids" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="expenseCategory"
            label="Төрөл"
            rules={[{ required: true, message: "Төрөл Хоосон" }]}
          >
            <Select
              allowClear
              placeholder="Төрлөөр шүүх"
              value={category}
              // onChange={(v) => setCategory(v)}
              options={categoryOptions}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Дүн"
            rules={[{ required: true, message: "Дүн Хоосон" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <Form.Item
            name="paymentType"
            label="Төлбөр"
            rules={[{ required: true, message: "Төлбөр Хоосон" }]}
          >
            <Select
              allowClear
              options={[
                { value: "CASH", label: "Бэлэн" },
                { value: "ACCOUNT", label: "Данс" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="date"
            label="Огноо"
            rules={[{ required: true, message: "Огноо Хоосон" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="supplier" label="Нийлүүлэгч (optional)">
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Тайлбар (optional)">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
